/**
 * Offline Maps Service
 * 
 * Manages offline map downloads using Mapbox OfflineManager
 * Tracks downloaded regions and provides download/delete functionality
 */

import { Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';

// Mapbox types and modules (only on native)
let OfflineManager: any = null;
let OfflinePack: any = null;
let OfflinePackState: any = null;

if (Platform.OS !== 'web') {
  try {
    const mapboxModule = require('@rnmapbox/maps');
    // Try different ways to access OfflineManager based on @rnmapbox/maps version
    // It might be a named export or accessed through the default export
    if (mapboxModule.offlineManager) {
      OfflineManager = mapboxModule.offlineManager;
    } else if (mapboxModule.OfflineManager) {
      OfflineManager = mapboxModule.OfflineManager;
    } else if (mapboxModule.default?.offlineManager) {
      OfflineManager = mapboxModule.default.offlineManager;
    } else if (mapboxModule.default?.OfflineManager) {
      OfflineManager = mapboxModule.default.OfflineManager;
    } else if (mapboxModule.default && typeof mapboxModule.default.getOfflineManager === 'function') {
      // Some versions use a getter function
      OfflineManager = mapboxModule.default.getOfflineManager();
    }

    OfflinePack = mapboxModule.OfflinePack;
    OfflinePackState = mapboxModule.OfflinePackState;

    if (__DEV__) {
      const keys = Object.keys(mapboxModule);
      const defaultKeys = mapboxModule.default ? Object.keys(mapboxModule.default) : [];
      console.log('[OfflineMaps] Mapbox module keys', { keys, defaultKeys, hasOfflineManager: !!OfflineManager });
    }
  } catch (error) {
    console.warn('[OfflineMaps] Mapbox offline not available:', error);
  }
}

export type OfflineMapRegion = {
  id: string;
  name: string;
  bounds: {
    ne: [number, number]; // [longitude, latitude]
    sw: [number, number]; // [longitude, latitude]
  };
  minZoom: number;
  maxZoom: number;
  styleURL: string;
  downloadedAt?: number;
  size?: number; // Size in bytes
};

export type DownloadProgress = {
  percentage: number;
  completedResourceCount: number;
  requiredResourceCount: number;
  completedTileCount: number;
  requiredTileCount: number;
};

const STORAGE_KEY = '@tripflow_offline_maps';
const CACHED_PLACES_KEY_PREFIX = '@tripflow_cached_places_';
const DEFAULT_MIN_ZOOM = 10;
const DEFAULT_MAX_ZOOM = 16;

const normalizeRegionLookupKey = (value?: string | null): string => {
  const raw = `${value || ''}`.trim().toLowerCase();
  if (!raw) return '';
  const primary = raw.split(',')[0]?.trim() || raw;
  return primary.replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, ' ').trim();
};

export const findDownloadedRegionForCity = (
  regions: OfflineMapRegion[],
  city: string | null | undefined,
): OfflineMapRegion | null => {
  const cityKey = normalizeRegionLookupKey(city);
  if (!cityKey) return null;
  for (const region of regions) {
    const regionKey = normalizeRegionLookupKey(region.name);
    if (!regionKey) continue;
    if (regionKey === cityKey) return region;
    if (regionKey.startsWith(`${cityKey} `) || cityKey.startsWith(`${regionKey} `)) {
      return region;
    }
  }
  return null;
};

/**
 * Get all downloaded offline map regions
 */
export const getDownloadedRegions = async (): Promise<OfflineMapRegion[]> => {
  try {
    const stored = await AsyncStorage.getItem(STORAGE_KEY);
    if (!stored) return [];
    
    const regions = JSON.parse(stored) as OfflineMapRegion[];
    return regions;
  } catch (error) {
    console.error('[OfflineMaps] Error getting downloaded regions:', error);
    return [];
  }
};

/**
 * Check if a region is downloaded
 */
export const isRegionDownloaded = async (regionId: string): Promise<boolean> => {
  const regions = await getDownloadedRegions();
  return regions.some(r => r.id === regionId);
};

/**
 * Save downloaded region metadata
 */
const saveRegionMetadata = async (region: OfflineMapRegion): Promise<void> => {
  try {
    const regions = await getDownloadedRegions();
    const existingIndex = regions.findIndex(r => r.id === region.id);
    
    if (existingIndex >= 0) {
      regions[existingIndex] = region;
    } else {
      regions.push(region);
    }
    
    await AsyncStorage.setItem(STORAGE_KEY, JSON.stringify(regions));
  } catch (error) {
    console.error('[OfflineMaps] Error saving region metadata:', error);
  }
};

/**
 * Remove region metadata
 */
const removeRegionMetadata = async (regionId: string): Promise<void> => {
  try {
    const regions = await getDownloadedRegions();
    const filtered = regions.filter(r => r.id !== regionId);
    await AsyncStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));
  } catch (error) {
    console.error('[OfflineMaps] Error removing region metadata:', error);
  }
};

/**
 * Get Mapbox style URL
 */
const getStyleURL = (): string => {
  try {
    const { getStyleURL } = require('@/config/mapbox');
    return getStyleURL();
  } catch (error) {
    console.warn('[OfflineMaps] Using default style URL');
    return 'mapbox://styles/mapbox/streets-v12';
  }
};

/**
 * Download offline map for a region
 */
export const downloadOfflineMap = async (
  region: Omit<OfflineMapRegion, 'id' | 'downloadedAt' | 'size'>,
  onProgress?: (progress: DownloadProgress) => void
): Promise<string> => {
  if (Platform.OS === 'web') {
    throw new Error('Offline maps are not supported on web platform');
  }

  if (!OfflineManager) {
    let reason = 'Offline maps require the Mapbox native module. Please use a dev/client build (expo prebuild && npx pod-install, then npx expo run:ios / run:android, or an EAS dev build).';
    try {
      // Expo Go / store clients do not include OfflineManager
      const Constants = require('expo-constants');
      if (Constants?.appOwnership === 'expo' || Constants?.executionEnvironment === 'storeClient') {
        reason = 'Offline maps are not available in Expo Go. Build and run a dev client (expo prebuild && npx pod-install, then npx expo run:ios / run:android, or an EAS dev build).';
      }
    } catch {}
    throw new Error(reason);
  }

  try {
    const styleURL = region.styleURL || getStyleURL();
    const regionId = `offline_${region.name.toLowerCase().replace(/\s+/g, '_')}_${Date.now()}`;

    // Create offline pack definition
    // @rnmapbox/maps uses bounds as [sw, ne] where sw and ne are [lon, lat]
    const packOptions = {
      name: region.name,
      styleURL,
      bounds: [
        region.bounds.sw, // Southwest [lon, lat]
        region.bounds.ne, // Northeast [lon, lat]
      ],
      minZoom: region.minZoom || DEFAULT_MIN_ZOOM,
      maxZoom: region.maxZoom || DEFAULT_MAX_ZOOM,
    };

    // Create the offline pack with progress callback
    // The callback signature may vary by version, so we handle both
    const progressCallback = (progress: any, pack: any) => {
      if (onProgress) {
        // Handle different progress object structures
        const state = progress?.state || progress;
        const downloadProgress: DownloadProgress = {
          percentage: state?.percentage || progress?.percentage || 0,
          completedResourceCount: state?.completedResourceCount || progress?.completedResourceCount || 0,
          requiredResourceCount: state?.requiredResourceCount || progress?.requiredResourceCount || 0,
          completedTileCount: state?.completedTileCount || progress?.completedTileCount || 0,
          requiredTileCount: state?.requiredTileCount || progress?.requiredTileCount || 0,
        };
        onProgress(downloadProgress);
      }
    };

    // Create the pack - API may vary by version
    let pack: any;
    if (typeof OfflineManager.createPack === 'function') {
      pack = await OfflineManager.createPack(packOptions, progressCallback);
    } else if (typeof OfflineManager.getPack === 'function') {
      // Some versions use getPack with create option
      pack = await OfflineManager.getPack(region.name);
      if (!pack) {
        pack = await OfflineManager.createPack(packOptions, progressCallback);
      }
    } else {
      throw new Error('OfflineManager.createPack is not available');
    }

    // Save metadata
    const regionMetadata: OfflineMapRegion = {
      ...region,
      id: regionId,
      styleURL,
      downloadedAt: Date.now(),
      size: pack?.metadata?.size || undefined,
    };

    await saveRegionMetadata(regionMetadata);

    return regionId;
  } catch (error) {
    console.error('[OfflineMaps] Error downloading offline map:', error);
    throw error;
  }
};

/**
 * Delete offline map region
 */
export const deleteOfflineMap = async (regionId: string): Promise<void> => {
  if (Platform.OS === 'web') {
    throw new Error('Offline maps are not supported on web platform');
  }

  if (!OfflineManager) {
    let reason = 'Offline maps require the Mapbox native module. Please use a dev/client build (expo prebuild && npx pod-install, then npx expo run:ios / run:android, or an EAS dev build).';
    try {
      const Constants = require('expo-constants');
      if (Constants?.appOwnership === 'expo' || Constants?.executionEnvironment === 'storeClient') {
        reason = 'Offline maps are not available in Expo Go. Build and run a dev client (expo prebuild && npx pod-install, then npx expo run:ios / run:android, or an EAS dev build).';
      }
    } catch {}
    throw new Error(reason);
  }

  try {
    // Get region metadata
    const regions = await getDownloadedRegions();
    const region = regions.find(r => r.id === regionId);
    
    if (!region) {
      throw new Error('Region not found');
    }

    // Delete the pack by name
    // API may vary: deletePack(name) or deletePack(pack)
    if (typeof OfflineManager.deletePack === 'function') {
      await OfflineManager.deletePack(region.name);
    } else if (typeof OfflineManager.removePack === 'function') {
      await OfflineManager.removePack(region.name);
    } else {
      // Try to get the pack first, then delete
      const pack = await OfflineManager.getPack(region.name);
      if (pack && typeof pack.cancel === 'function') {
        await pack.cancel();
      }
    }

    // Remove metadata
    await removeRegionMetadata(regionId);
  } catch (error) {
    console.error('[OfflineMaps] Error deleting offline map:', error);
    throw error;
  }
};

/**
 * Remove all downloaded offline map regions (best-effort per region).
 */
export const deleteAllOfflineMaps = async (): Promise<void> => {
  if (Platform.OS === 'web') {
    return;
  }
  let regions = await getDownloadedRegions();
  let guard = 0;
  while (regions.length > 0 && guard < 200) {
    guard += 1;
    const r = regions[0];
    try {
      await deleteOfflineMap(r.id);
    } catch (error) {
      console.warn('[OfflineMaps] deleteAllOfflineMaps: failed for region', r.id, error);
      await removeRegionMetadata(r.id).catch(() => {});
    }
    regions = await getDownloadedRegions();
  }
};

/**
 * Get download progress for a region
 */
export const getDownloadProgress = async (regionId: string): Promise<DownloadProgress | null> => {
  if (Platform.OS === 'web' || !OfflineManager) {
    return null;
  }

  try {
    const regions = await getDownloadedRegions();
    const region = regions.find(r => r.id === regionId);
    
    if (!region) {
      return null;
    }

    // Get the pack - API may vary
    let pack: any;
    if (typeof OfflineManager.getPack === 'function') {
      pack = await OfflineManager.getPack(region.name);
    } else if (typeof OfflineManager.getPacks === 'function') {
      const packs = await OfflineManager.getPacks();
      pack = packs.find((p: any) => p.name === region.name);
    } else {
      return null;
    }
    
    if (!pack) {
      return null;
    }

    // Get state - may be pack.state or pack.getStatus()
    const state = pack.state || (typeof pack.getStatus === 'function' ? await pack.getStatus() : null);
    if (!state) {
      return null;
    }

    return {
      percentage: state.percentage || 0,
      completedResourceCount: state.completedResourceCount || 0,
      requiredResourceCount: state.requiredResourceCount || 0,
      completedTileCount: state.completedTileCount || 0,
      requiredTileCount: state.requiredTileCount || 0,
    };
  } catch (error) {
    console.error('[OfflineMaps] Error getting download progress:', error);
    return null;
  }
};

/**
 * Get total size of all downloaded offline maps
 */
export const getTotalDownloadSize = async (): Promise<number> => {
  try {
    const regions = await getDownloadedRegions();
    return regions.reduce((total, region) => total + (region.size || 0), 0);
  } catch (error) {
    console.error('[OfflineMaps] Error calculating total size:', error);
    return 0;
  }
};

/**
 * Format file size in human-readable format
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

/**
 * Pre-cache map tiles for a trip route
 * Downloads map tiles along the route connecting all itinerary items
 */
export const precacheTripRoute = async (
  trip: {
    id: string;
    name: string;
    destination?: string;
    destinations?: Array<{ city: string; coordinates?: { latitude: number; longitude: number } }>;
  },
  itineraryItems: Array<{
    location?: string;
    address?: string;
    coordinates?: { latitude: number; longitude: number };
  }>,
  onProgress?: (progress: { completed: number; total: number; current: string }) => void
): Promise<void> => {
  if (Platform.OS === 'web') {
    console.warn('[OfflineMaps] Pre-caching not supported on web');
    return;
  }

  try {
    // Collect all coordinates from itinerary items
    const coordinates: Array<{ latitude: number; longitude: number }> = [];

    // Add destination coordinates
    if (trip.destinations && trip.destinations.length > 0) {
      trip.destinations.forEach(dest => {
        if (dest.coordinates) {
          coordinates.push(dest.coordinates);
        }
      });
    }

    // Add itinerary item coordinates
    itineraryItems.forEach(item => {
      if (item.coordinates) {
        coordinates.push(item.coordinates);
      }
    });

    if (coordinates.length === 0) {
      console.warn('[OfflineMaps] No coordinates found for trip route');
      return;
    }

    // Calculate bounding box
    const lats = coordinates.map(c => c.latitude);
    const lngs = coordinates.map(c => c.longitude);
    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    const minLng = Math.min(...lngs);
    const maxLng = Math.max(...lngs);

    // Add padding to bounding box (10% on each side)
    const latPadding = (maxLat - minLat) * 0.1;
    const lngPadding = (maxLng - minLng) * 0.1;

    const bounds = {
      ne: [maxLng + lngPadding, maxLat + latPadding] as [number, number],
      sw: [minLng - lngPadding, minLat - latPadding] as [number, number],
    };

    // Download offline map for the route region
    const regionName = `${trip.name} Route`;
    
    if (onProgress) {
      onProgress({ completed: 0, total: 1, current: 'Preparing download...' });
    }

    await downloadOfflineMap(
      {
        name: regionName,
        bounds,
        minZoom: DEFAULT_MIN_ZOOM,
        maxZoom: DEFAULT_MAX_ZOOM,
        styleURL: getStyleURL(),
      },
      (progress) => {
        if (onProgress) {
          onProgress({
            completed: progress.completedTileCount,
            total: progress.requiredTileCount,
            current: `Downloading tiles: ${Math.round(progress.percentage)}%`,
          });
        }
      }
    );

    console.log(`[OfflineMaps] Pre-cached route for trip: ${trip.name}`);
  } catch (error) {
    console.error('[OfflineMaps] Error pre-caching trip route:', error);
    throw error;
  }
};


/**
 * Search for places offline using cached data
 */
export const searchOfflinePlaces = async (
  query: string,
  tripId: string
): Promise<Array<{
  id: string;
  name: string;
  category: string;
  coordinates: { latitude: number; longitude: number };
  address?: string;
}>> => {
  try {
    // Get cached places for the trip
    // This would typically be stored when places are fetched for a trip
    const cachedPlacesKey = `${CACHED_PLACES_KEY_PREFIX}${tripId}`;
    const cached = await AsyncStorage.getItem(cachedPlacesKey);
    
    if (!cached) {
      return [];
    }

    const places = JSON.parse(cached) as Array<{
      id: string;
      name: string;
      category: string;
      coordinates: { latitude: number; longitude: number };
      address?: string;
      description?: string;
      tags?: string[];
    }>;

    // Simple text search
    const queryLower = query.toLowerCase();
    const results = places.filter(place => {
      const nameMatch = place.name.toLowerCase().includes(queryLower);
      const addressMatch = place.address?.toLowerCase().includes(queryLower);
      const descriptionMatch = place.description?.toLowerCase().includes(queryLower);
      const tagMatch = place.tags?.some(tag => tag.toLowerCase().includes(queryLower));
      
      return nameMatch || addressMatch || descriptionMatch || tagMatch;
    });

    return results.map(place => ({
      id: place.id,
      name: place.name,
      category: place.category,
      coordinates: place.coordinates,
      address: place.address,
    }));
  } catch (error) {
    console.error('[OfflineMaps] Error searching offline places:', error);
    return [];
  }
};

/** Remove cached offline place search blobs for all trips (e.g. on sign-out). */
export async function clearCachedPlacesStorageKeys(): Promise<void> {
  try {
    const keys = await AsyncStorage.getAllKeys();
    const toRemove = keys.filter((k) => k.startsWith(CACHED_PLACES_KEY_PREFIX));
    if (toRemove.length > 0) {
      await AsyncStorage.multiRemove(toRemove);
    }
  } catch (error) {
    console.error('[OfflineMaps] Error clearing cached places keys:', error);
  }
}

/**
 * Cache places for a trip (to enable offline search)
 */
export const cachePlacesForTrip = async (
  tripId: string,
  places: Array<{
    id: string;
    name: string;
    category: string;
    coordinates: { latitude: number; longitude: number };
    address?: string;
    description?: string;
    tags?: string[];
  }>
): Promise<void> => {
  try {
    const cachedPlacesKey = `${CACHED_PLACES_KEY_PREFIX}${tripId}`;
    await AsyncStorage.setItem(cachedPlacesKey, JSON.stringify(places));
    console.log(`[OfflineMaps] Cached ${places.length} places for trip ${tripId}`);
  } catch (error) {
    console.error('[OfflineMaps] Error caching places for trip:', error);
    throw error;
  }
};

